import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSubmitGcRequirements from "../../../hooks/useSubmitGcRequirements";
import "./styles/GcRequirements.scss";

import {
  sections as fallbackSections,
  TOTAL_REQUIREMENTS,
} from "./data/requirementsData";
import useGetGcRequirements from "../../../hooks/useGetGcRequirements";
import useGetProject from "../../../hooks/useGetProject";
import useScrollToTop from "../../../hooks/useScrollToTop";

import ProjectBanner from "./components/ProjectBanner";
import IntroCard from "./components/IntroCard";
import AckProgress from "./components/AckProgress";
import RequirementSection from "./components/RequirementSection";
import RequirementSectionSkeleton from "./components/RequirementSectionSkeleton";
import Skeleton from "../../../Components/Common/Skeleton/Skeleton";
import SignatureSection from "./components/SignatureSection";
import type { SignatureSectionHandle } from "./components/SignatureSection";
import SubmitBar from "./components/SubmitBar";
import SuccessPage from "./components/SuccessPage";
import CustomModal from "../../../Components/Common/CustomModal/CustomModal";
import { useGcLayout } from "../GcLayout";
import NetworkError from "Components/Common/NetworkError/NetworkError";
import Button from "Components/Common/Button/Button";

interface GcRequirementsProps {
  onContinue?: () => void;
  projectId?: string;
}

const GcRequirements = ({
  onContinue,
  projectId: projectIdProp,
}: GcRequirementsProps) => {
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = projectIdProp ?? projectIdParam;
  const { setCrumbs } = useGcLayout();

  useEffect(() => {
    setCrumbs([
      { label: "My Projects", link: "/gc-dashboard" },
      { label: "Project Requirements" },
    ]);
  }, []);
  const navigate = useNavigate();
  const {
    sections: apiSections,
    totalCount,
    isEditable,
    ack,
    loading,
    error: fetchError,
  } = useGetGcRequirements(projectId);
  const {
    submitRequirements,
    loading: submitting,
    error: submitError,
  } = useSubmitGcRequirements(projectId);

  const [sections, setSections] = useState<any[]>(fallbackSections);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (apiSections.length > 0) setSections(apiSections);
  }, [apiSections]);
  useEffect(() => {
    if (ack) sigRef.current?.prefill(ack);
  }, [ack]);

  const { projectData, loading: projectLoading } = useGetProject(projectId) as {
    projectData: any;
    loading: boolean;
    error: string;
  };
  const sigRef = useRef<SignatureSectionHandle>(null);
  const [hasSig, setHasSig] = useState<boolean>(false);
  const [hasDesignation, setHasDesignation] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  useScrollToTop(submitted ? 1 : 0);
  const [showBlocker, setShowBlocker] = useState<boolean>(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  // Optional rows (e.g. Performance & Payment Bond) are excluded from mandatory counts.
  const optionalCount = useMemo(
    () =>
      sections.flatMap((s: any) => s.rows).filter((r: any) => r.isOptional)
        .length,
    [sections],
  );
  const total: number = projectId
    ? Math.max(0, totalCount - optionalCount)
    : TOTAL_REQUIREMENTS;
  const ackCount = useMemo(
    () =>
      sections
        .flatMap((s: any) => s.rows)
        .filter((r: any) => r.isAccepted === true && !r.isOptional).length,
    [sections],
  );
  const allAcknowledged = ackCount === total && total > 0;

  const toggleAck = useCallback(
    (id: string) => {
      if (!isEditable) return;
      setShowBlocker(false);
      setSections((prev: any[]) =>
        prev.map((section) => ({
          ...section,
          rows: section.rows.map((row: any) =>
            row.id === id
              ? { ...row, isAccepted: !(row.isAccepted === true) }
              : row,
          ),
        })),
      );
    },
    [isEditable],
  );

  const getSubmitIds = () => {
    const allRows = sections.flatMap((s: any) => s.rows);
    return {
      acknowledgedIds: new Set(
        allRows.filter((r: any) => r.isAccepted === true).map((r: any) => r.id),
      ),
      allIds: allRows.map((r: any) => r.id),
    };
  };

  const handleSubmit = () => {
    if (!allAcknowledged || !hasSig) {
      setShowBlocker(true);
      return;
    }
    setShowModal(true);
  };

  const cleanupModal = () => {
    document.body.classList.remove("modal-open");
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    document.body.style.removeProperty("padding-right");
    document.body.style.removeProperty("overflow");
    document.body.removeAttribute("style");
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const sigData = await sigRef.current?.getSubmitData();
      const { acknowledgedIds, allIds } = getSubmitIds();
      const { success } = await submitRequirements({
        acknowledgedIds,
        allIds,
        sigData,
      });
      if (success) {
        setShowModal(false);
        cleanupModal();
        setSubmitted(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    if (!submitting) {
      setShowModal(false);
      cleanupModal();
    }
  };

  const handleSaveReturn = async () => {
    if (projectId) {
      setIsSaving(true);
      try {
        const rawSigData = await sigRef.current?.getSubmitData();
        const sigData = rawSigData
          ? (Object.fromEntries(
              Object.entries(rawSigData).filter(
                ([, v]) => v !== "" && v !== null && v !== undefined,
              ),
            ) as typeof rawSigData)
          : rawSigData;
        const { acknowledgedIds, allIds } = getSubmitIds();
        await submitRequirements({
          acknowledgedIds,
          allIds,
          sigData,
          publish: false,
        });
      } finally {
        setIsSaving(false);
      }
    }
    navigate(`/gc-dashboard`);
  };
  if (submitted) {
    return (
      <div className="gcRequirements">
        <div className="gcReqPage">
          <SuccessPage
            onContinue={() =>
              navigate(`/gc-precon/${projectId}`, {
                state: { preconType: projectData?.projectType },
              })
            }
            projectData={projectData}
            gcName={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="gcRequirements">
      <div className="gcReqPage">
        {projectLoading ? (
          <div className="projBanner">
            <div className="projBanner__left">
              <Skeleton
                width={100}
                height={22}
                style={{ marginBottom: 10 }}
                borderRadius="6px"
              />
              <Skeleton width="70%" height={13} borderRadius={undefined} />
            </div>
            <div className="projBanner__right">
              <Skeleton
                width={52}
                height={12}
                style={{ marginBottom: 6 }}
                borderRadius="4px"
              />
              <Skeleton width={120} height={16} borderRadius="4px" />
            </div>
          </div>
        ) : (
          <ProjectBanner
            name={projectData?.projectName ?? ""}
            type={projectData?.projectType ?? ""}
            address={projectData?.projectAddress ?? ""}
            company={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()}
            lenderLabel="Lender"
            lenderName={projectData?.lender?.companyName ?? ""}
          />
        )}

        <IntroCard
          lenderName={projectData?.lender?.companyName ?? ""}
          adminName={`${projectData?.admin?.firstName ?? ""} ${projectData?.admin?.lastName ?? ""}`.trim()}
        />

        <AckProgress count={ackCount} total={total} />

        {loading ? (
          <>
            <RequirementSectionSkeleton />
            <RequirementSectionSkeleton />
            <RequirementSectionSkeleton />
          </>
        ) : fetchError ? (
          <NetworkError />
        ) : (
          sections.map((section) => (
            <RequirementSection
              key={section.id}
              section={section}
              onToggle={toggleAck}
            />
          ))
        )}

        <SignatureSection
          ref={sigRef}
          onSigChange={setHasSig}
          onDesignationChange={setHasDesignation}
        />

        {showBlocker && (
          <div className="blockerNote">
            Please acknowledge all requirements and add your signature before
            submitting.
          </div>
        )}
      </div>

      <SubmitBar
        count={ackCount}
        total={total}
        onSaveReturn={handleSaveReturn}
        onSubmit={handleSubmit}
        disabled={!allAcknowledged || !hasSig || !hasDesignation || isSaving}
        isSaving={isSaving}
      />

      <CustomModal
        show={showModal}
        handleClose={handleCloseModal}
        title="Confirm Acknowledgment"
        isHeaderVisible={true}
        isCrossBtn={false}
        isFooterVisible={true}
        footerContent={
          <>
            <Button
              className="btn btn-secondary"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Review Again
            </Button>
            <Button
              className="btn btn-primary"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner" /> Submitting…
                </>
              ) : (
                "Confirm & Sign"
              )}
            </Button>
          </>
        }
      >
        <p>
          You are about to formally acknowledge all {total} project requirements
          for <strong>{projectData?.projectName ?? ""}</strong> (
          {projectData?.lender?.companyName ?? ""}). This acknowledgment is
          legally binding and will be attached to the project record. Please
          confirm you have read and understood each requirement.
        </p>
        {submitError && (
          <p
            style={{
              color: "var(--red, #a32d2d)",
              fontSize: 13,
              marginTop: 10,
            }}
          >
            {submitError}
          </p>
        )}
      </CustomModal>
    </div>
  );
};

export default GcRequirements;
